import * as React from "react";
import { useTranslation } from "react-i18next";
import Svg, { Circle, Defs, G, Mask, Polygon, Rect } from "react-native-svg";

type Props = {
  size?: number;
  outerColor?: string;
  innerColor?: string;
  starColor?: string;
  label?: string;
  style?: any;
};

const DefsAny = Defs as unknown as React.ComponentType<{
  children?: React.ReactNode;
}>;

const N_PETALS = 16;
const PETAL_RING_R = 42;
const PETAL_R = 6.5;
const RING_INNER_R = 30;
const RING_OUTER_R = 42;
const CENTER_R = 22;
const STAR_OUTER_R = 15;
const STAR_INNER_R = 6;

const PETAL_CENTERS = Array.from({ length: N_PETALS }, (_, i) => {
  const angle = (2 * Math.PI * i) / N_PETALS;
  return {
    cx: 50 + PETAL_RING_R * Math.cos(angle),
    cy: 50 + PETAL_RING_R * Math.sin(angle),
  };
});

const STAR_POINTS = Array.from({ length: 10 }, (_, i) => {
  const r = i % 2 === 0 ? STAR_OUTER_R : STAR_INNER_R;
  const angle = -Math.PI / 2 + (i * Math.PI) / 5;
  return `${(50 + r * Math.cos(angle)).toFixed(2)},${(
    50 + r * Math.sin(angle)
  ).toFixed(2)}`;
}).join(" ");

const VerifiedBadge: React.FC<Props> = ({
  size = 16,
  outerColor = "#C8102E",
  innerColor = "#0038A8",
  starColor = "#FFFFFF",
  label,
  style,
  ...props
}) => {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t("VERIFIED_USER");
  const rawId = React.useId();
  const uid = rawId.replace(/[^a-zA-Z0-9]/g, "");
  const ringMaskId = `vb-ring-${uid}`;

  return (
    <Svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      style={style}
      title={resolvedLabel}
      accessibilityLabel={resolvedLabel}
      accessible
      {...props}
    >
      <DefsAny>
        <Mask id={ringMaskId} x={0} y={0} width={100} height={100}>
          <Rect x={0} y={0} width={100} height={100} fill="white" />
          <Circle cx={50} cy={50} r={RING_INNER_R} fill="black" />
        </Mask>
      </DefsAny>

      <G mask={`url(#${ringMaskId})`}>
        {PETAL_CENTERS.map((c, i) => (
          <Circle key={i} cx={c.cx} cy={c.cy} r={PETAL_R} fill={outerColor} />
        ))}
        <Circle cx={50} cy={50} r={RING_OUTER_R} fill={outerColor} />
      </G>

      <Circle cx={50} cy={50} r={CENTER_R} fill={innerColor} />
      <Polygon points={STAR_POINTS} fill={starColor} />
    </Svg>
  );
};

export default VerifiedBadge;
